import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const checkInProgress = useRef(false); // Track if check is running
  const navigate = useNavigate();
  const location = useLocation();

  const checkOnboardingStatus = async () => {
    const currentPath = location.pathname;
    
    // Skip check if not logged in
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    
    // Prevent multiple simultaneous checks using ref (more reliable than state)
    if (checkInProgress.current) {
      console.log('⏳ Onboarding check already in progress, skipping...');
      return;
    }
    
    checkInProgress.current = true;
    setLoading(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/auth/onboarding-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setOnboardingStatus(response.data);
      
      // Skip redirects if user is already on an onboarding page
      // This prevents redirect loops
      if (currentPath.includes('/admin/locations') || 
          currentPath.includes('/admin/subscription') ||
          currentPath.includes('/admin/exports') ||
          currentPath.includes('/admin/unterweisungen/export') ||
          currentPath.includes('/admin/temperature-export') ||
          currentPath.includes('/admin/mhd-export') ||
          currentPath.includes('/admin/backshop-export') ||
          currentPath.includes('/admin/bestellungen-export') ||
          currentPath.includes('/admin/allgemein-export') ||
          currentPath.includes('/admin/task-report') ||
          currentPath.includes('/auth/') || 
          currentPath.includes('/login') || 
          currentPath.includes('/register') ||
          currentPath.includes('/superadmin')) {
        console.log('ℹ️ ONBOARDING GUARD: User already on onboarding page, skipping redirect');
        return;
      }
      
      // Only redirect ADMIN users who need onboarding (NOT stationsleitung or employee)
      if (response.data.needs_onboarding && response.data.role === 'admin') {
        
        // Spezialfall: Gekündigte Kunden (is_canceled) → NUR zum Exporte-Modul, kein Onboarding-Flow
        if (response.data.is_canceled) {
          if (!currentPath.includes('/admin/exports') && !currentPath.startsWith('/admin/subscription')) {
            console.log('🔒 ONBOARDING GUARD: Abonnement gekündigt → Weiterleitung zu Exporten');
            navigate('/admin/exports-overview', { replace: true });
          }
          return;
        }

        // Priority 1: No location - HARD REDIRECT to location creation
        if (!response.data.has_location) {
          console.log('🚨 ONBOARDING GUARD: Redirecting to location creation (no location)');
          navigate('/admin/locations/new', { replace: true });
          return;
        }
        
        // Priority 2: No subscription - HARD REDIRECT to subscription page
        if (!response.data.has_active_subscription) {
          console.log('🚨 ONBOARDING GUARD: Redirecting to subscription (no active subscription)');
          navigate('/admin/subscription', { replace: true });
          return;
        }
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    } finally {
      setLoading(false);
      checkInProgress.current = false;
    }
  };

  useEffect(() => {
    checkOnboardingStatus();
  }, [location.pathname]); // Re-check on route changes

  const value = {
    onboardingStatus,
    loading,
    refreshOnboardingStatus: checkOnboardingStatus
  };

  // Show loading state while checking onboarding status
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};
